import React, { useMemo, Children, isValidElement, forwardRef } from 'react'
import ReactSelect, { SingleValue, StylesConfig } from 'react-select'

export interface SelectOption {
  value: string
  label: string
}

/**
 * Drop-in replacement for native <select> with searchable dropdown functionality.
 *
 * Usage — same as a regular <select>:
 * ```tsx
 * <SearchableSelect value={x} onChange={(e) => setX(e.target.value)}>
 *   <option value=''>All</option>
 *   <option value='1'>One</option>
 *   {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
 * </SearchableSelect>
 * ```
 *
 * Props mirror native <select>: value, onChange (synthesizes native event),
 * disabled, name, className, placeholder.
 */
interface SearchableSelectProps {
  children?: React.ReactNode
  value?: any
  onChange?: (e: any) => void
  onBlur?: (...args: any[]) => void
  onFocus?: (...args: any[]) => void
  disabled?: boolean
  name?: string
  className?: string
  placeholder?: string
  id?: string
  required?: boolean
  style?: React.CSSProperties
  size?: number
  onInputChange?: (value: string) => void
  onMenuScrollToBottom?: () => void
}

const SearchableSelect = forwardRef<any, SearchableSelectProps>((
  {
    children,
    value,
    onChange,
    onBlur,
    onFocus,
    disabled = false,
    name,
    className,
    placeholder = 'Type to search...',
    id,
    required,
    style,
    onInputChange,
    onMenuScrollToBottom,
  },
  ref
) => {
  const isSmallSelect = (className || '').split(/\s+/).includes('form-select-sm')
  const isLargeSelect = (className || '').split(/\s+/).includes('form-select-lg')
  const normalizedClassName = useMemo(() => {
    const nativeSelectClasses = new Set(['form-select', 'form-select-sm', 'form-select-lg'])
    return [
      ...(className || '').split(/\s+/).filter((item) => item && !nativeSelectClasses.has(item)),
      'searchable-select',
    ].join(' ')
  }, [className])

  /** Parse <option> children into react-select options */
  const options = useMemo(() => {
    const arr: SelectOption[] = []
    
    const extractLabel = (node: React.ReactNode): string => {
      if (node === null || node === undefined) return ''
      if (typeof node === 'string' || typeof node === 'number') return String(node)
      if (Array.isArray(node)) return node.map(extractLabel).join('')
      if (isValidElement(node)) return extractLabel((node.props as any)?.children)
      return String(node)
    }

    const extractOptions = (node: React.ReactNode) => {
      Children.forEach(node, (child) => {
        if (!isValidElement(child)) return
        if (child.type === 'option') {
          const { value: optVal, children: optChildren, disabled: optDisabled } = child.props
          if (optDisabled) return
          const label = extractLabel(optChildren) || String(optVal ?? '')
          arr.push({
            value: String(optVal ?? ''),
            label,
          })
        } else if (child.props && child.props.children) {
          extractOptions(child.props.children)
        }
      })
    }

    extractOptions(children)
    return arr
  }, [children])

  /** Find selected option matching the current value */
  const selected = useMemo(
    () => options.find((opt) => String(opt.value) === String(value)) ?? null,
    [options, value]
  )

  /** Synthesize a change event mimicking native <select> */
  const handleChange = (opt: SingleValue<SelectOption>) => {
    if (!onChange) return
    const newValue = opt?.value ?? ''
    onChange({
      target: {
        value: newValue,
        name: name || '',
        type: 'select-one',
      },
      currentTarget: {
        value: newValue,
        name: name || '',
      },
      preventDefault: () => { },
      stopPropagation: () => { },
    })
  }

  /** Synthesize a blur event mimicking native <select> */
  const handleBlur = (e: any) => {
    if (!onBlur) return
    const currentVal = selected?.value ?? (value != null ? String(value) : '')
    onBlur({
      ...e,
      target: {
        ...(e?.target || {}),
        value: currentVal,
        name: name || '',
        type: 'select-one',
      },
      currentTarget: {
        ...(e?.currentTarget || {}),
        value: currentVal,
        name: name || '',
      },
      preventDefault: () => { },
      stopPropagation: () => { },
    })
  }

  const customStyles: StylesConfig<SelectOption, false> = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#3b82f6' : base.borderColor,
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(59,130,246,.25)' : 'none',
      minHeight: isSmallSelect ? 31 : isLargeSelect ? 44 : 38,
      borderRadius: 8,
      '&:hover': { borderColor: state.isFocused ? '#3b82f6' : base.borderColor },
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 10000,
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      zIndex: 10000,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#3b82f6'
        : state.isFocused
          ? '#eff6ff'
          : 'white',
      color: state.isSelected ? 'white' : '#1e293b',
      '&:active': { backgroundColor: state.isSelected ? '#2563eb' : '#dbeafe' },
    }),
    placeholder: (base) => ({ ...base, color: '#94a3b8', fontSize: 14 }),
    singleValue: (base) => ({ ...base, color: '#1e293b', fontSize: 14 }),
    input: (base) => ({ ...base, fontSize: 14 }),
    indicatorSeparator: () => ({ display: 'none' }),
    clearIndicator: (base) => ({
      ...base,
      cursor: 'pointer',
      color: '#94a3b8',
      '&:hover': { color: '#64748b' },
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      cursor: 'pointer',
      color: state.isFocused ? '#3b82f6' : '#94a3b8',
      '&:hover': { color: '#3b82f6' },
    }),
  }

  return (
    <ReactSelect<SelectOption, false>
      className={normalizedClassName}
      classNamePrefix='react-select'
      options={options}
      value={selected}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={onFocus}
      onInputChange={(value) => {
        onInputChange?.(value)
        return value
      }}
      onMenuScrollToBottom={onMenuScrollToBottom}
      placeholder={placeholder}
      isDisabled={disabled}
      isClearable={false}
      isSearchable
      inputId={id}
      required={required}
      styles={customStyles}
      menuPortalTarget={document.body}
      menuPosition='fixed'
      ref={ref}
    />
  )
})

export default SearchableSelect
